import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { Heading, HeadingLevel } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { getChildBackgroundColourToken } from '@/lib/colourUtils';
import { colourTokens } from '@/styles/colours';

import type { SanityPolicyListType, SanityPolicyType } from '@/types/sanity';

export type PolicyListProps = Omit<SanityPolicyListType, 'policyLinks'> & {
  policyLinks?: Array<
    Omit<SanityPolicyListType['policyLinks'][0], 'policy'> & {
      policy?: {
        slug?: SanityPolicyType['slug'];
      };
    }
  >;
};

const PolicyList = ({
  policyLinks,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
}: PolicyListProps) => {
  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';
  const childBackgroundColourToken = getChildBackgroundColourToken(
    backgroundColourToken,
    '--background-warm-base',
  );
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="policy-list"
    >
      <ListContainer>
        {policyLinks.map((policyLink) => (
          <PolicyLink
            backgroundColourToken={childBackgroundColourToken}
            key={policyLink._key}
          >
            <HeadingLevel>
              <Heading size="heading-md">{policyLink.name}</Heading>
              {policyLink.policy && (
                <Button
                  href={`/our-policies/${policyLink.policy.slug.current}`}
                  variant="solid-light"
                  className={
                    policyLink.trackingTag?.blockName &&
                    `tracking-${policyLink.trackingTag.blockName}__${policyLink.trackingTag.blockAction}`
                  }
                >
                  Read more
                </Button>
              )}
            </HeadingLevel>
          </PolicyLink>
        ))}
      </ListContainer>
    </Section>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 var(--spacing-500);
  padding: var(--spacing-300) 0;

  ${screen.md} {
    padding: var(--spacing-small) 0;
  }
`;

const PolicyLink = styled.div<{ backgroundColourToken }>`
  background-color: ${({ backgroundColourToken }) =>
    `var(${backgroundColourToken});`};
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-500);
  margin-bottom: var(--spacing-300);
  border-radius: var(--radius-m);
  width: 100%;
  flex-direction: column;

  ${Heading} {
    margin-bottom: var(--spacing-300);
    color: var(${colourTokens.text.action.token});
    text-align: center;
  }

  ${screen.md} {
    flex-direction: row;
    align-items: center;
    max-width: 1000px;

    ${Heading} {
      margin: 0;
      text-align: left;
    }

    &:first-of-type {
      margin-top: var(--spacing-800);
    }
  }
  a {
    flex-shrink: 0;
  }
`;

export { PolicyList };
